// The step designer: the form you declare a workflow's capture steps on, and
// the subform designer beside it.
//
// Nothing here is generated — /Input/Steps and /Input/Subforms are ordinary
// schemas and these are ordinary schema-driven forms over them. All the file
// really contains is the `config` blob saying which fields to show and in
// what order, plus the two selects whose options are not knowable from the
// schema alone: a field's type comes from the registry, and an `object`
// field's subform comes from what the Subforms tab has declared.
//
// It is @repoflow/components/types-form's config, split the other way. That
// component puts a section's "root" type on one tab and the rest on a
// "Subforms" tab of its own, inside one form. Here a workflow has MANY roots
// — every step is compiled as root — so the steps are one list, the subforms
// are a second list held on the workflow, and the two are separate forms the
// editor puts on separate tabs. What is kept verbatim is the `/Types/Param`
// block — the label-fills-in-name hop, the type select, the per-type config —
// because that is the part a person spends their time in, and it should feel
// the same in both apps.
import React, { useMemo } from "react";
import _ from "lodash";
import { Component as FormSchema } from "@repoflow/form-schema";
import { slugify } from "@repoflow/components/types-form";
import StepsSchema from "@common/schema/types";
import { types as paramTypes } from "@common/schema/types/params/utils";

// Built once per set of subform NAMES, never per render: a config literal is a
// new object every render and the form's provider rebuilds its widget tree
// underneath it, which detaches the control being typed into. Same reason
// every screen hoists its field lists. The one input that can change is the
// list of subforms an object field may name, and that changes when a subform
// is added or renamed — a modal submit, not a keystroke — so `useMemo` on the
// names is as good as a hoist.
const paramOptions = () => [
  { value: "", label: "Select a type" },
  ..._.map(paramTypes, (type) => ({ value: type, label: type })),
];

const typeConfig = {
  property: "type",
  widget: "select",
  options: paramOptions,
};

// Typing a label fills in the name. Two hops up because the field's parent is
// the label field's own wrapper, not the row.
const labelFillsName = {
  property: "label",
  onChange: ({ value, field: { parent: { parent: { setValues } } } }) => {
    setValues({ name: slugify(value) });
  },
};

// The Details column of the steps and subforms listings: how many fields a
// row has and which, in place of the table the list widget would nest there.
// The edit form keeps the list widget — this override is on the listing only.
const FIELDS_SUMMARY = { property: "params", widget: "paramsSummary" };

/**
 * The designer's config, offering `subformNames` to an `object` field.
 *
 * Exported for the test that pins it against the schema; the forms below
 * build it through `useConfig`.
 */
export const buildConfig = (subformNames = []) => ({
  "/Input/Steps": {
    fields: [{ property: "steps", layout: "wide" }],
  },
  "/Input/Subforms": {
    fields: [{ property: "subforms", layout: "wide" }],
  },
  "/Workflow/Subform": {
    listing: ["name", "description", FIELDS_SUMMARY],
    fields: [
      "name",
      { property: "description", layout: "wide", textarea: true },
      { property: "params", layout: "wide" },
    ],
  },
  "/Types/Param/Type/Object": {
    fields: [
      {
        property: "type",
        widget: "select",
        // The subforms the workflow has declared. From the Subforms tab, not
        // from this form's own values: a step's form cannot see them any
        // other way, and a subform's form could only see itself.
        options: () => [
          { value: "", label: "Select a subform" },
          ...subformNames.map((name) => ({ value: name, label: name })),
        ],
      },
      "nullable",
    ],
  },
  "/Workflow/Step": {
    listing: ["name", "label", FIELDS_SUMMARY],
    fields: [
      labelFillsName,
      "name",
      { property: "description", layout: "wide", textarea: true },
      { property: "params", layout: "wide" },
    ],
  },
  "/Types/Param": {
    // Name, type, and what the type was given — the `config` branch, as the
    // one-line summary every branch document names: `paramDetails`, in
    // @repoflow/components/widgets/param-details. common/schema/types/params
    // /types says why the schema names it rather than this list.
    listing: ["name", "type", "config"],
    fields: [
      labelFillsName,
      "name",
      typeConfig,
      { property: "config", layout: "wide" },
      { property: "description", layout: "wide", textarea: true },
      "required",
      "hidden",
      "layout",
    ],
  },
  "/Types/Param/Type/List": {
    fields: [typeConfig, { property: "config", layout: "wide" }],
  },
  // Both of these exist to HIDE `default`, which each schema declares so that
  // the generated property carries one but neither offers: the only default
  // worth having on a capture field is "today" or "now", and a schema document
  // cannot say that without depending on the clock. What is left is the one
  // question each type actually has.
  "/Types/Param/Type/Date": {
    fields: [
      {
        property: "format",
        widget: "select",
        // The enum values are `date` and `date-time`, which are the JSON Schema
        // keywords the widget reads and not words to put in front of a person
        // designing a form.
        options: () => [
          { value: "date", label: "A calendar date" },
          { value: "date-time", label: "A date and a time" },
        ],
      },
    ],
  },
  "/Types/Param/Type/Time": {
    fields: ["interval"],
  },
  "/Types/Param/Type/Options": {
    fields: [
      "list",
      {
        property: "default",
        widget: "select",
        // The default has to be one of the options just declared, so the
        // choices come from a sibling value rather than from the schema.
        options: ({ field }) => {
          const parent = field.parent ? field.parent.parent : null;
          const empty = [{ value: "", label: "Empty" }];

          if (!parent || !parent.values || !parent.values.list) {
            return empty;
          }

          return [
            ...empty,
            ...parent.values.list.map((value) => ({ value, label: value })),
          ];
        },
      },
      "allowEmpty",
    ],
  },
});

/** The config with no subform to offer — what the test pins. */
export const CONFIG = buildConfig();

// One string, so the memo compares by content rather than by array identity:
// the caller hands a fresh array in every render.
const useConfig = (subforms) => {
  const names = _.map(subforms, "name").filter(Boolean).join("\n");
  return useMemo(() => buildConfig(names ? names.split("\n") : []), [names]);
};

const STEPS = { $ref: "/Input/Steps" };
const SUBFORMS = { $ref: "/Input/Subforms" };

/**
 * The designer over `{ steps }`.
 *
 * `values` is the whole `{ steps: [...] }` object and `onChange` is handed the
 * same shape back, so the caller writes it onto its own form state under the
 * one key. `subforms` is the workflow's, read-only here: it is what an
 * `object` field on a step may name.
 */
export const Form = ({ values, onChange, widgets, subforms = [], readOnly = false }) => (
  <FormSchema
    schema={StepsSchema}
    config={useConfig(subforms)}
    target={STEPS}
    widgets={widgets}
    readOnly={readOnly}
    values={values}
    onChange={onChange}
  />
);

/**
 * The subform designer over `{ subforms }`, the same way.
 *
 * The names offered to an `object` field here are the same list's own — a
 * subform may nest another, or itself. They come from `values` rather than
 * from a prop so that a subform added a moment ago is offered to the next.
 */
export const SubformsForm = ({ values, onChange, widgets, readOnly = false }) => (
  <FormSchema
    schema={StepsSchema}
    config={useConfig(values.subforms)}
    target={SUBFORMS}
    widgets={widgets}
    readOnly={readOnly}
    values={values}
    onChange={onChange}
  />
);
